import { productTaxonomyCopyCatalog, productTaxonomyTermCatalog } from './renames';
import type {
  ProductTaxonomyCopyEntry,
  ProductTaxonomyCopyTermReference,
  ProductTaxonomyStage,
  ProductTaxonomyVariant,
} from './types';

export type { ProductTaxonomyStage, ProductTaxonomyVariant } from './types';

// `final` reuses the `future` catalog language; only the optional per-copy
// `final` override (resolved below) diverges from `future`.
function resolveVariant(stage: ProductTaxonomyStage): ProductTaxonomyVariant {
  return stage === 'final' ? 'future' : stage;
}

export type ProductTaxonomyTermKey = keyof typeof productTaxonomyTermCatalog;
type ProductTaxonomyTermEntry = (typeof productTaxonomyTermCatalog)[ProductTaxonomyTermKey][ProductTaxonomyVariant];
export type ProductTaxonomyTermFormKey = keyof ProductTaxonomyTermEntry;
export type ProductTaxonomyTermReference = `${ProductTaxonomyTermKey & string}.${ProductTaxonomyTermFormKey & string}`;

export type ProductTaxonomyCopyKey = keyof typeof productTaxonomyCopyCatalog;

export function resolveProductTaxonomyTerm(
  reference: ProductTaxonomyTermReference,
  stage: ProductTaxonomyStage,
): string {
  const variant = resolveVariant(stage);
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const [termKey, formKey] = reference.split('.') as [ProductTaxonomyTermKey, ProductTaxonomyTermFormKey];
  return productTaxonomyTermCatalog[termKey][variant][formKey];
}

export function resolveProductTaxonomyCopy(key: ProductTaxonomyCopyKey, stage: ProductTaxonomyStage): string {
  // The `as const` catalog narrows entries to their literal shapes, which drops the
  // optional `final` override; read through the declared entry type to recover it.
  const entry: ProductTaxonomyCopyEntry = productTaxonomyCopyCatalog[key];

  if ('term' in entry) {
    return resolveProductTaxonomyCopyTerm(entry.term, stage);
  }

  if (stage === 'final') {
    return entry.final ?? entry.future;
  }

  return entry[stage];
}

function resolveProductTaxonomyCopyTerm(
  reference: ProductTaxonomyCopyTermReference,
  stage: ProductTaxonomyStage,
): string {
  const variant = resolveVariant(stage);
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const [termKey, formKey] = reference.split('.') as [string, ProductTaxonomyTermFormKey];
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const value = productTaxonomyTermCatalog[termKey as ProductTaxonomyTermKey]?.[variant][formKey];

  if (value == null) {
    throw new Error(`Unknown product taxonomy term reference: ${reference}`);
  }

  return value;
}
